import type { ReactNode } from 'react';

/**
 * Toolbar illustrations (P5d, backlog §10.1): each creation tool shows its object (a yellow note, a shape pair, two
 * linked nodes…) instead of a small grey glyph. Drawn for this app on a 32 px grid; strokes follow the button's
 * colour, so the pressed (accent) state stays legible, and only the note keeps its own paper colour.
 */
export type ToolArtKind = 'sticky' | 'shape' | 'connector' | 'text' | 'section' | 'table' | 'tasklist' | 'comment' | 'plus';

const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const ART: Record<ToolArtKind, ReactNode> = {
  sticky: (
    <g transform="rotate(-5 16 16)">
      <path d="M8 5.5h16a2 2 0 0 1 2 2V19l-7.5 7.5H8a2 2 0 0 1-2-2v-17a2 2 0 0 1 2-2z" fill="#f6d55c" />
      <path d="M26 19h-5.5a2 2 0 0 0-2 2v5.5z" fill="#ddb43a" />
      <path d="M10 11.5h11M10 15.5h7.5" stroke="#7a5f12" strokeWidth={1.5} strokeLinecap="round" />
    </g>
  ),
  shape: (
    <>
      <rect x="4.5" y="12" width="14.5" height="14.5" rx="3" {...line} fill="currentColor" fillOpacity={0.16} />
      <circle cx="21" cy="11" r="6.75" fill="currentColor" fillOpacity={0.4} stroke="none" />
    </>
  ),
  connector: (
    <>
      <circle cx="8" cy="23.5" r="3.25" {...line} />
      <rect x="20.5" y="4.5" width="7" height="7" rx="2" {...line} />
      <path d="M10.75 21.25C13.5 14 17 17.5 19.5 10.5" {...line} />
      <path d="M16.6 11.2 19.5 10.5 20.4 13.4" {...line} />
    </>
  ),
  text: <path d="M7.5 9.5V6.5h17v3M16 6.5v19.5M12 26h8" {...line} strokeWidth={2.25} />,
  section: (
    <>
      <rect x="4.5" y="9.5" width="23" height="17" rx="2.5" {...line} strokeDasharray="3 2.6" />
      <rect x="4.5" y="4.5" width="12" height="5.5" rx="2.75" fill="currentColor" />
    </>
  ),
  table: (
    <>
      <path d="M4.5 9.5a2.5 2.5 0 0 1 2.5-2.5h18a2.5 2.5 0 0 1 2.5 2.5v3.5h-23z" fill="currentColor" fillOpacity={0.35} />
      <rect x="4.5" y="7" width="23" height="18.5" rx="2.5" {...line} />
      <path d="M4.5 13h23M4.5 19.25h23M12.25 7v18.5M19.75 7v18.5" {...line} strokeWidth={1.25} />
    </>
  ),
  tasklist: (
    <>
      <rect x="4.5" y="5" width="23" height="22" rx="2.5" {...line} fill="currentColor" fillOpacity={0.12} />
      <rect x="8" y="9" width="5" height="5" rx="1.25" fill="currentColor" />
      <path d="M9.25 11.5l1 1 1.75-2" stroke="var(--color-surface, #fff)" strokeWidth={1.25} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.5 11.5h8" {...line} />
      <rect x="8" y="17.5" width="5" height="5" rx="1.25" {...line} strokeWidth={1.25} />
      <path d="M15.5 20h6" {...line} />
    </>
  ),
  comment: (
    <>
      <path d="M7 6.5h18a2.5 2.5 0 0 1 2.5 2.5v10.5a2.5 2.5 0 0 1-2.5 2.5h-10l-5.5 4.5V22H7a2.5 2.5 0 0 1-2.5-2.5V9A2.5 2.5 0 0 1 7 6.5z" {...line} fill="currentColor" fillOpacity={0.16} />
      <path d="M11 12.5h10M11 16.5h6" {...line} />
    </>
  ),
  plus: (
    <>
      <circle cx="16" cy="16" r="11" fill="currentColor" fillOpacity={0.12} />
      <path d="M16 10.5v11M10.5 16h11" {...line} strokeWidth={2.25} />
    </>
  ),
};

export function ToolArt({ kind, size = 28 }: { kind: ToolArtKind; size?: number }) {
  return (
    <svg data-tool-art={kind} aria-hidden="true" viewBox="0 0 32 32" width={size} height={size} className="shrink-0">
      {ART[kind]}
    </svg>
  );
}
